export const AD_TEMPLATE_KIND = {
  STATIC: "static",
  VIDEO: "video",
};

export const STATIC_AD_TEMPLATE_IDS = [
  "whatsapp",
  "igStory",
  "disculpa",
  "noticia",
  "textPost",
  "commentThread",
  "reviewCapture",
  "searchAutocomplete",
  "checklist",
  "emailCapture",
];

// String values match keysTranslations.js so node:test can load this module.
export const STATIC_AD_TEMPLATE_NAME_KEYS = {
  whatsapp: "modalCreateWithIAAdTemplateNameWhatsapp",
  igStory: "modalCreateWithIAAdTemplateNameIgStory",
  disculpa: "modalCreateWithIAAdTemplateNameApology",
  noticia: "modalCreateWithIAAdTemplateNameNews",
  textPost: "modalCreateWithIAAdTemplateNameTextPost",
  commentThread: "modalCreateWithIAAdTemplateNameCommentThread",
  reviewCapture: "modalCreateWithIAAdTemplateNameReviewCapture",
  searchAutocomplete: "modalCreateWithIAAdTemplateNameSearchAutocomplete",
  checklist: "modalCreateWithIAAdTemplateNameChecklist",
  emailCapture: "modalCreateWithIAAdTemplateNameEmailCapture",
};

export const STATIC_AD_TEMPLATE_EXPLANATION_KEYS = {
  textPost: "modalCreateWithIAAdTemplateExplanationTextPost",
  commentThread: "modalCreateWithIAAdTemplateExplanationCommentThread",
  reviewCapture: "modalCreateWithIAAdTemplateExplanationReviewCapture",
  searchAutocomplete:
    "modalCreateWithIAAdTemplateExplanationSearchAutocomplete",
  checklist: "modalCreateWithIAAdTemplateExplanationChecklist",
  emailCapture: "modalCreateWithIAAdTemplateExplanationEmailCapture",
};

const translateTemplateCopy = ({ key, fallback, translate }) => {
  if (!key || typeof translate !== "function") return fallback;
  return translate(key);
};

export const resolveAdTemplateOptionLabel = ({
  templateID,
  name = "",
  translate,
}) =>
  translateTemplateCopy({
    key: STATIC_AD_TEMPLATE_NAME_KEYS[templateID],
    fallback: name,
    translate,
  });

export const resolveAdTemplateOptionExplanation = ({
  templateID,
  explanation = "",
  translate,
}) =>
  translateTemplateCopy({
    key: STATIC_AD_TEMPLATE_EXPLANATION_KEYS[templateID],
    fallback: explanation,
    translate,
  });

export const shouldLockHasTextForAdTemplate = ({ templateID, mediaType }) => {
  const id = typeof templateID === "string" ? templateID.trim() : "";
  if (!id) return false;
  return mediaType === "image" || mediaType === "carousel";
};

/** Shared catalog: contentGeneratorAdTemplates/{templateID} */
export const CONTENT_GENERATOR_AD_TEMPLATES_NODE =
  "contentGeneratorAdTemplates";

export const getContentGeneratorAdTemplatesPath = () =>
  CONTENT_GENERATOR_AD_TEMPLATES_NODE;

export const AD_TEMPLATE_NAME_MAX_LENGTH = 80;
export const AD_TEMPLATE_EXPLANATION_MAX_LENGTH = 2000;

const AD_TEMPLATE_KINDS = new Set([
  AD_TEMPLATE_KIND.STATIC,
  AD_TEMPLATE_KIND.VIDEO,
]);

export const resolveAdTemplateKind = ({ mediaType }) =>
  mediaType === "video" ? AD_TEMPLATE_KIND.VIDEO : AD_TEMPLATE_KIND.STATIC;

const readRequiredString = ({
  value,
  maxLength = Number.POSITIVE_INFINITY,
}) => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > maxLength) return "";
  return trimmed;
};

/**
 * One shape only. Aliases such as id, popularity, or mediaKind are ignored
 * and never fill a missing canonical field.
 */
export const parseContentGeneratorAdTemplate = (template) => {
  if (!template || typeof template !== "object" || Array.isArray(template)) {
    return null;
  }

  const templateID = readRequiredString({ value: template.templateID });
  const name = readRequiredString({
    value: template.name,
    maxLength: AD_TEMPLATE_NAME_MAX_LENGTH,
  });
  const explanation = readRequiredString({
    value: template.explanation,
    maxLength: AD_TEMPLATE_EXPLANATION_MAX_LENGTH,
  });
  const kind = template.kind;
  const source = readRequiredString({ value: template.source });
  const { usageCount } = template;
  const recommendedAspectRatio =
    template.recommendedAspectRatio == null
      ? ""
      : template.recommendedAspectRatio;

  if (!templateID || !name || !explanation || !source) return null;
  if (!AD_TEMPLATE_KINDS.has(kind)) return null;
  if (typeof usageCount !== "number" || !Number.isFinite(usageCount)) {
    return null;
  }
  if (typeof recommendedAspectRatio !== "string") return null;

  return {
    templateID,
    name,
    explanation,
    kind,
    source,
    usageCount,
    recommendedAspectRatio,
  };
};

export const parseContentGeneratorAdTemplatesNode = ({ value, kind } = {}) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];

  const templates = Object.values(value)
    .map((entry) => parseContentGeneratorAdTemplate(entry))
    .filter((template) => template && (!kind || template.kind === kind));

  return templates.toSorted((left, right) => {
    const usageDelta = right.usageCount - left.usageCount;
    if (usageDelta !== 0) return usageDelta;
    return left.name.localeCompare(right.name);
  });
};

export const buildAdTemplateGeneratePayload = ({ templateID }) => {
  const id = typeof templateID === "string" ? templateID.trim() : "";
  if (!id) return null;
  return { templateID: id };
};

export const isRecommendedAspectRatioValid = ({
  recommendedAspectRatio,
  allowedAspectRatios,
}) =>
  typeof recommendedAspectRatio === "string" &&
  recommendedAspectRatio.length > 0 &&
  Array.isArray(allowedAspectRatios) &&
  allowedAspectRatios.includes(recommendedAspectRatio);

export const shouldShowRecommendedAspectRatioCaption = ({
  aspectRatio,
  recommendedAspectRatio,
  allowedAspectRatios,
}) =>
  isRecommendedAspectRatioValid({
    recommendedAspectRatio,
    allowedAspectRatios,
  }) && recommendedAspectRatio !== aspectRatio;
